import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import Login, { destinationFor } from "@/pages/Login";
import Register, { ChooseRole } from "@/pages/Register";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import WorkerHome from "@/pages/WorkerHome";
import WorkerOpportunities from "@/pages/WorkerOpportunities";
import WorkerRequests from "@/pages/WorkerRequests";
import WorkerCalendar from "@/pages/WorkerCalendar";
import WorkerAvailability from "@/pages/WorkerAvailability";
import WorkerProfileForm from "@/pages/WorkerProfile";
import ShiftDetail from "@/pages/ShiftDetail";
import PharmacyDashboard from "@/pages/PharmacyDashboard";
import PostShift from "@/pages/PostShift";
import PharmacyStaffing from "@/pages/PharmacyStaffing";
import PharmacyShiftMatches from "@/pages/PharmacyShiftMatches";
import PharmacyProfileForm from "@/pages/PharmacyProfile";
import NpiOnboarding from "@/pages/NpiOnboarding";
import { AdminDashboard, AdminAccounts, AdminWorkers, AdminPharmacies, AdminShifts, AdminMatches } from "@/pages/Admin";
import { AdminReports, AdminLegal } from "@/pages/AdminLegal";
import Settings from "@/pages/Settings";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import AcceptableUse from "@/pages/legal/AcceptableUse";
import Contact from "@/pages/legal/Contact";

const Loading = () => (
  <div className="flex min-h-screen items-center justify-center">
    <Loader2 className="h-6 w-6 animate-spin text-brand" />
  </div>
);

function Protected({ role, children, bare = false, npiStep = false }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.has_consent || !user.role) return <Navigate to="/choose-role" replace />;
  if (role && user.role !== role) return <Navigate to={destinationFor(user)} replace />;
  if (user.npi_locked && !npiStep) return <Navigate to={`/${user.role}/npi`} replace />;
  return bare ? children : <AppShell>{children}</AppShell>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  if (user) return <Navigate to={destinationFor(user)} replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/choose-role" element={<ChooseRole />} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />

      <Route path="/legal/terms" element={<Terms />} />
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/legal/acceptable-use" element={<AcceptableUse />} />
      <Route path="/legal/contact" element={<Contact />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/acceptable-use" element={<AcceptableUse />} />
      <Route path="/contact" element={<Contact />} />

      <Route path="/settings" element={<Protected><Settings /></Protected>} />

      <Route path="/worker/npi" element={<Protected role="worker" npiStep bare><NpiOnboarding mode="worker" /></Protected>} />
      <Route path="/pharmacy/npi" element={<Protected role="pharmacy" npiStep bare><NpiOnboarding mode="pharmacy" /></Protected>} />

      <Route path="/worker" element={<Protected role="worker"><WorkerHome /></Protected>} />
      <Route path="/worker/onboarding" element={<Protected role="worker"><WorkerProfileForm onboarding /></Protected>} />
      <Route path="/worker/opportunities" element={<Protected role="worker"><WorkerOpportunities /></Protected>} />
      <Route path="/worker/requests" element={<Protected role="worker"><WorkerRequests /></Protected>} />
      <Route path="/worker/calendar" element={<Protected role="worker"><WorkerCalendar /></Protected>} />
      <Route path="/worker/availability" element={<Protected role="worker"><WorkerAvailability /></Protected>} />
      <Route path="/worker/profile" element={<Protected role="worker"><WorkerProfileForm /></Protected>} />
      <Route path="/worker/shifts/:shiftId" element={<Protected role="worker"><ShiftDetail /></Protected>} />

      <Route path="/pharmacy" element={<Protected role="pharmacy"><PharmacyDashboard /></Protected>} />
      <Route path="/pharmacy/onboarding" element={<Protected role="pharmacy"><PharmacyProfileForm onboarding /></Protected>} />
      <Route path="/pharmacy/post" element={<Protected role="pharmacy"><PostShift /></Protected>} />
      <Route path="/pharmacy/staffing" element={<Protected role="pharmacy"><PharmacyStaffing /></Protected>} />
      <Route path="/pharmacy/calendar" element={<Protected role="pharmacy"><PharmacyStaffing calendar /></Protected>} />
      <Route path="/pharmacy/profile" element={<Protected role="pharmacy"><PharmacyProfileForm /></Protected>} />
      <Route path="/pharmacy/shifts/:shiftId" element={<Protected role="pharmacy"><PharmacyShiftMatches /></Protected>} />

      <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
      <Route path="/admin/accounts" element={<Protected role="admin"><AdminAccounts /></Protected>} />
      <Route path="/admin/workers" element={<Protected role="admin"><AdminWorkers /></Protected>} />
      <Route path="/admin/pharmacies" element={<Protected role="admin"><AdminPharmacies /></Protected>} />
      <Route path="/admin/shifts" element={<Protected role="admin"><AdminShifts /></Protected>} />
      <Route path="/admin/matches" element={<Protected role="admin"><AdminMatches /></Protected>} />
      <Route path="/admin/reports" element={<Protected role="admin"><AdminReports /></Protected>} />
      <Route path="/admin/legal" element={<Protected role="admin"><AdminLegal /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
